/**
 * El mes del panel --o el año entero, que también se puede mirar--.
 *
 * El periodo se escribe `AAAA-MM` en todo el sistema (§7.3.5) y aquí no se
 * inventa otra forma. Un año suelto (`2026`) no es otro formato: es la misma
 * cadena sin el mes, y significa "todos los meses de ese año". Ningún endpoint dice qué meses existen --no hay tal
 * consulta en el catálogo-- así que las opciones salen del calendario, que
 * para esto es fuente suficiente: el mes de hoy y los anteriores.
 */
import { PERIOD_CHOICES } from "../lib/limits.ts";

const pad = (value: number): string => String(value).padStart(2, "0");

/** El mes de hoy, como lo escribe el resto del sistema. */
export const currentPeriod = (now: Date = new Date()): string =>
  `${String(now.getFullYear())}-${pad(now.getMonth() + 1)}`;

/**
 * Los meses que ofrece el selector, del más reciente al más antiguo.
 *
 * Se resta sobre el número de mes y no sobre la fecha: `setMonth(-1)` en un
 * día 31 salta al mes equivocado, y aquí el día no importa.
 */
export const recentPeriods = (
  count: number = PERIOD_CHOICES,
  now: Date = new Date(),
): readonly string[] => {
  const periods: string[] = [];
  const year = now.getFullYear();
  const month = now.getMonth();

  for (let back = 0; back < count; back += 1) {
    const at = new Date(year, month - back, 1);
    periods.push(currentPeriod(at));
  }

  return periods;
};

/** `2026-09` como lo diría una persona. Para los sitios donde hay espacio. */
export const periodLabel = (period: string): string => {
  const [year, month] = period.split("-");
  if (year === undefined || month === undefined) return period;

  const at = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(at.getTime())) return period;

  return at.toLocaleDateString("es", { month: "long", year: "numeric" });
};

/** Cuatro cifras y nada más: el año entero. */
const WHOLE_YEAR_PATTERN = /^\d{4}$/;
const YEAR_LENGTH = 4;

/**
 * ¿Lo que hay elegido es un año entero?
 *
 * Lo preguntan las secciones que son MENSUALES --el Resumen lo es: su
 * cobertura, sus lotes y sus mecánicas se piden mes a mes-- para decirlo en
 * vez de pedirle al servidor un periodo que no entiende.
 */
export const isWholeYear = (period: string): boolean =>
  WHOLE_YEAR_PATTERN.test(period);

export const yearOf = (period: string): string => period.slice(0, YEAR_LENGTH);

/** Los años que aparecen en los meses que ofrece el selector, sin repetir. */
export const recentYears = (
  months: readonly string[] = recentPeriods(),
): readonly string[] => [...new Set(months.map(yearOf))];

/**
 * Cómo se lee una opción del selector.
 *
 * El año lleva "Todo" delante porque sin eso `2026` y `2026-06` se distinguen
 * por un detalle tipográfico, y lo que cambia entre los dos es qué se está
 * mirando.
 */
export const periodChoiceLabel = (period: string): string =>
  isWholeYear(period) ? `Todo ${period}` : period;

/**
 * Del periodo del panel a dos fechas `AAAA-MM-DD`, las dos INCLUIDAS.
 *
 * Lo pide lo que se consulta por rango de días --el consumo (2.4)-- y no por
 * periodo. Un año entero va del 1 de enero al 31 de diciembre. El último día
 * del mes sale del calendario (el día 0 del mes siguiente) y no de una tabla.
 */
export const periodBounds = (
  period: string,
): { readonly from: string; readonly to: string } => {
  const year = Number(yearOf(period));
  if (isWholeYear(period)) {
    return { from: `${String(year)}-01-01`, to: `${String(year)}-12-31` };
  }

  const month = Number(period.slice(YEAR_LENGTH + 1));
  const lastDay = new Date(year, month, 0).getDate();
  return {
    from: `${period}-01`,
    to: `${period}-${pad(lastDay)}`,
  };
};
